import React, { useEffect, useMemo, useState, useContext, useCallback } from 'react';
import { Context } from '../../../context/UserContext';

import { useNavigateParams } from '../../../hooks/useNavigateParams';
import { MaterialReactTable } from 'material-react-table';

import { ThemeProvider } from '@mui/material';
import tableThemeCustom from '../../form/tableThemeCustom';

import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR';

import { Box, IconButton, Tooltip, Typography, Divider } from '@mui/material';
import BarChartIcon from '@mui/icons-material/BarChart';
import { Print } from '@mui/icons-material';
import tratar from '../../../utils/retornaTratamentoURL';
import CrudsAPI from '../../../api/CRUDs';

import { obterPermissoesUsuario, printPdf, resolveEmpresaTabela } from '../../../utils/funcoes';
import { getEmpresaScope } from '../../../helpers/sessionEmpresa';
import { useMRTState } from '../../../hooks/useMRTState';
import { podeImprimir } from '../../../hooks/usePermisionMRT';

const Index = () => {
    const [dados, setDados] = useState([]);
    const [token] = useState(localStorage.getItem('token') || '');

    const navigate = useNavigateParams();
    const urlParams = new URLSearchParams(window.location.search);

    const ul = urlParams.get('ul') || 3;
    const li = urlParams.get('li') || 53;

    const { parametro } = useContext(Context);
    const { pessoaId, empresaId } = getEmpresaScope();
    const empresaTabelaId = resolveEmpresaTabela(parametro, empresaId);

    const mrt = useMRTState({
        storageKey: 'balancete.index',
        pageSizeDefault: parametro?.paginacao ?? 10,
        pageIndexDefault: 0,
    });

    useEffect(() => {
        if (!parametro?.paginacao) return;

        mrt.setPagination((prev) => {
            if (prev?.pageSize && prev.pageSize !== 0) return prev;
            return {
                ...prev,
                pageSize: parametro.paginacao,
            };
        });
    }, [parametro?.paginacao]);

    useEffect(() => {
        const currentPath = window.location.pathname;
        const savedPath = JSON.parse(localStorage.getItem('path') || '""');

        if (savedPath !== currentPath) {
            tratar.LimparFiltros();
            localStorage.setItem('path', JSON.stringify(currentPath));
        }
    }, []);

    useEffect(() => {
        if (!token) {
            navigate('/Auth', { replace: true });
            return;
        }

        obterPermissoesUsuario(pessoaId, empresaId, ul, li);
    }, [token, pessoaId, empresaId, ul, li, navigate]);

    const fetchData = useCallback(async () => {
        if (mrt.rowCount === 0) {
            mrt.setIsLoading(true);
        } else {
            mrt.setIsRefetching(true);
        }

        try {
            const { data } = await CrudsAPI.balancete.listar(
                mrt.pagination.pageIndex * mrt.pagination.pageSize,
                mrt.pagination.pageSize,
                JSON.stringify(mrt.columnFilters ?? []),
                JSON.stringify(mrt.columnFilterFns ?? []),
                mrt.globalFilter ?? '',
                JSON.stringify(mrt.sorting ?? [])
            );

            setDados(data?.data || []);
            mrt.setRowCount(data?.meta?.totalRowCount || 0);
            mrt.setIsError(false);
        } catch (error) {
            console.error(error);
            mrt.setIsError(true);
        } finally {
            mrt.setIsLoading(false);
            mrt.setIsRefetching(false);
        }
    }, [
        mrt.rowCount,
        mrt.pagination.pageIndex,
        mrt.pagination.pageSize,
        mrt.columnFilters,
        mrt.columnFilterFns,
        mrt.globalFilter,
        mrt.sorting,
    ]);

    useEffect(() => {
        if (!token) return;
        fetchData();
    }, [token, fetchData]);

    // helpers
    const getMonthName = (monthNumber) => {
        const months = [
            'Janeiro',
            'Fevereiro',
            'Março',
            'Abril',
            'Maio',
            'Junho',
            'Julho',
            'Agosto',
            'Setembro',
            'Outubro',
            'Novembro',
            'Dezembro',
        ];
        return months[monthNumber - 1];
    };
    const formatCurrencyBRL = (value) =>
        Number(value || 0).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
        });
    const formatDia = (value) => {
        if (!value) return '';
        const [ano, mes, dia] = String(value).split('-');
        return `${dia}/${mes}/${ano}`;
    };

    const columns = useMemo(
        () => [
            {
                id: 'Balancete',
                header: 'Balancete Financeiro',
                columns: [
                    {
                        accessorKey: 'ano',
                        header: 'Ano',
                        muiTableBodyCellProps: ({ row }) => ({
                            onClick: () => {
                                console.info(row.original);
                            },
                        }),
                        columnFilterModeOptions: ['fuzzy'],
                    },
                    {
                        accessorKey: 'valorReceber',
                        header: 'Lançamento Receber',
                        columnFilterModeOptions: [], //remove individualmente as opções
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.valorReceber === null
                                ? ''
                                : parseFloat(row.valorReceber)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'valorPagar',
                        header: 'Lançamento Pagar',
                        columnFilterModeOptions: [], //remove individualmente as opções
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.valorPagar === null
                                ? ''
                                : parseFloat(row.valorPagar)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'totalRecebido',
                        header: 'Valor Recebido',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.totalRecebido === null
                                ? ''
                                : parseFloat(row.totalRecebido)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'totalPago',
                        header: 'Valor Pago',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.totalPago === null
                                ? ''
                                : parseFloat(row.totalPago)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'resultado',
                        header: 'Recebido - Pago',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.resultado === null
                                ? ''
                                : parseFloat(row.resultado)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }),
                    },
                    {
                        accessorKey: 'resultadoPercentual',
                        header: 'Reebido/Pago %',
                        muiTableBodyCellProps: {
                            align: 'right',
                        },
                        accessorFn: (row) =>
                            row.resultadoPercentual === null
                                ? ''
                                : parseFloat(row.resultadoPercentual)?.toLocaleString('pt-BR', {
                                      style: 'decimal',
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                      minimumIntegerDigits: 1,
                                      useGrouping: true,
                                  }) + ' %',
                    },
                ],
            },

            //column definitions...
        ],
        [] //getCommonEditTextFieldProps
    );

    /* modo tema */
    const theme = tableThemeCustom();

    //ocultar detalhes de meses inicialmente
    const [mesesExpandidos, setMesesExpandidos] = useState({});
    const toggleMes = (ano, mes) => {
        const key = `${ano}-${mes}`;

        setMesesExpandidos((prev) => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const handleImprimirMes = async (rowOriginal, mesBaixa) => {
        try {
            const urlSearchParams = new URLSearchParams({
                start: `${mrt.pagination.pageIndex * mrt.pagination.pageSize}`,
                size: `${mrt.pagination.pageSize}`,
                filters: JSON.stringify(mrt.columnFilters ?? []),
                filtersMode: JSON.stringify(mrt.columnFilterFns ?? []),
                globalFilter: mrt.globalFilter ?? '',
                sorting: JSON.stringify(mrt.sorting ?? []),
                tipo: urlParams.get('tipo') ?? '',
            });

            const payload = {
                ano: rowOriginal.ano,
                mes: mesBaixa.mes,
                mesesBaixa: rowOriginal.mesesBaixa ?? [],
            };

            const response = await CrudsAPI.balancete.imprimirMes(urlSearchParams, payload);

            printPdf(response.data);
        } catch (error) {
            console.error('Erro ao imprimir mês:', error);
        }
    };

    const handleImprimirDRE = async (anoObj, mesObj = null) => {
        const payload = {
            empresaId: empresaId,
            ano: anoObj.ano,
            ...(mesObj?.mes && { mes: mesObj.mes }),
            obj: anoObj,
        };

        const response = await CrudsAPI.balancete.imprimirDRE({}, payload);

        printPdf(response.data);
    };
    return (
        <div>
            <ThemeProvider theme={theme}>
                <MaterialReactTable
                    displayColumnDefOptions={{
                        'mrt-row-actions': {
                            muiTableHeadCellProps: {
                                align: 'center',
                            },
                            size: 100,
                        },
                        'mrt-row-numbers': {
                            enableColumnOrdering: true, //turn on some features that are usually off
                            enableResizing: true,
                            muiTableHeadCellProps: {
                                sx: {
                                    fontSize: '1.2rem',
                                },
                            },
                        },
                        'mrt-row-select': {
                            enableColumnActions: true,
                            enableHiding: true,
                            size: 50,
                        },
                    }}
                    //enableColumnFilterModes
                    enableGlobalFilterModes
                    //enableColumnResizing
                    enableColumnOrdering
                    enableEditing
                    renderRowActions={({ row, table }) => {
                        const handleImprimir = async (obj) => {
                            const urlSearchParams = new URLSearchParams({
                                start: `${mrt.pagination.pageIndex * mrt.pagination.pageSize}`,
                                size: `${mrt.pagination.pageSize}`,
                                filters: JSON.stringify(mrt.columnFilters ?? []),
                                filtersMode: JSON.stringify(mrt.columnFilterFns ?? []),
                                globalFilter: mrt.globalFilter ?? '',
                                sorting: JSON.stringify(mrt.sorting ?? []),
                                tipo: urlParams.get('tipo') ?? '',
                            });

                            const response = await CrudsAPI.balancete.imprimir(urlSearchParams, obj);

                            printPdf(response.data);
                        };

                        return (
                            <Box sx={{ display: 'flex', gap: '1rem' }}>
                                <Tooltip arrow placement="right-end" title="Imprimir">
                                    <span>
                                        {' '}
                                        <IconButton onClick={() => handleImprimir(row.original)} disabled={!podeImprimir()}>
                                            <Print />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip arrow placement="right-end" title="Imprimir DRE">
                                    <span>
                                        {' '}
                                        <IconButton onClick={() => handleImprimirDRE(row.original)} disabled={!podeImprimir()}>
                                            <BarChartIcon />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Box>
                        );
                    }}
                    renderDetailPanel={({ row }) => (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                                gap: 2,
                                p: 2,
                            }}
                        >
                            {Array.isArray(row.original.mesesBaixa) && row.original.mesesBaixa.length > 0 ? (
                                row.original.mesesBaixa.map((mesBaixa, index) => (
                                    <Box
                                        key={`${row.original.ano}-${mesBaixa.mes}-${index}`}
                                        sx={{
                                            width: '100%',
                                            borderRadius: '8px',
                                            border: '1px solid rgba(255,255,255,0.12)',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        {/* Cabeçalho do mês */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                flexWrap: 'wrap',
                                                gap: 2,
                                                px: 2,
                                                py: 1.5,
                                                backgroundColor: '#353535',
                                                cursor: 'pointer',
                                            }}
                                            onClick={() => toggleMes(row.original.ano, mesBaixa.mes)}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    flexWrap: 'wrap',
                                                    gap: 2,
                                                }}
                                            >
                                                <Tooltip arrow placement="top" title="Imprimir mês">
                                                    <span>
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                // depois encaixamos a impressão do mês aqui
                                                                handleImprimirMes(row.original, mesBaixa);
                                                            }}
                                                            color="info"
                                                        >
                                                            <Print />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <Tooltip arrow placement="top" title="Imprimir DRE mês">
                                                    <span>
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleImprimirDRE(row.original, mesBaixa);
                                                            }}
                                                            color="info"
                                                        >
                                                            <BarChartIcon />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>

                                                <strong>
                                                    {mesesExpandidos[`${row.original.ano}-${mesBaixa.mes}`] ? '▼' : '▶'}{' '}
                                                    {getMonthName(mesBaixa.mes)}
                                                </strong>

                                                <span>Recebido {formatCurrencyBRL(mesBaixa.totalValorBaixaReceber)}</span>

                                                <span>Pago {formatCurrencyBRL(mesBaixa.totalValorBaixaPagar)}</span>

                                                <span>Resultado {formatCurrencyBRL(mesBaixa.resultado)}</span>
                                            </Box>
                                        </Box>

                                        {/* Detalhamento diário */}
                                        {mesesExpandidos[`${row.original.ano}-${mesBaixa.mes}`] && (
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    gap: 1,
                                                    p: 1.5,
                                                    backgroundColor: 'rgba(255,255,255,0.02)',
                                                }}
                                            >
                                                {Array.isArray(mesBaixa.diasBaixa) && mesBaixa.diasBaixa.length > 0 ? (
                                                    mesBaixa.diasBaixa.map((diaBaixa, idx) => (
                                                        <Box
                                                            key={`${diaBaixa.dia}-${idx}`}
                                                            sx={{
                                                                display: 'flex',
                                                                justifyContent: 'space-between',
                                                                alignItems: 'center',
                                                                flexWrap: 'wrap',
                                                                gap: 2,
                                                                px: 1.5,
                                                                py: 1,
                                                                borderRadius: '6px',
                                                                backgroundColor: 'rgba(255,255,255,0.04)',
                                                            }}
                                                        >
                                                            <Box sx={{ minWidth: 110 }}>
                                                                <strong>{formatDia(diaBaixa.dia)}</strong>
                                                            </Box>

                                                            <Box sx={{ minWidth: 180, textAlign: 'right' }}>
                                                                Recebido {formatCurrencyBRL(diaBaixa.totalValorBaixaReceber)}
                                                            </Box>

                                                            <Box sx={{ minWidth: 180, textAlign: 'right' }}>
                                                                Pago {formatCurrencyBRL(diaBaixa.totalValorBaixaPagar)}
                                                            </Box>

                                                            <Box sx={{ minWidth: 180, textAlign: 'right' }}>
                                                                Resultado {formatCurrencyBRL(diaBaixa.resultado)}
                                                            </Box>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Box sx={{ px: 1.5, py: 1 }}>
                                                        <span>Sem detalhamento diário.</span>
                                                    </Box>
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                ))
                            ) : (
                                <Box sx={{ p: 1 }}>
                                    <span>Sem detalhamento mensal.</span>
                                </Box>
                            )}
                        </Box>
                    )}
                    columns={columns}
                    data={dados}
                    //enableRowSelection
                    getRowId={(row) => row.uuid}
                    manualFiltering
                    manualPagination
                    manualSorting
                    muiToolbarAlertBannerProps={
                        mrt.isError
                            ? {
                                  color: 'error',
                                  children: 'Error loading data',
                              }
                            : undefined
                    }
                    onColumnFiltersChange={mrt.setColumnFilters}
                    onColumnFilterFnsChange={mrt.setColumnFiltersMode}
                    onGlobalFilterChange={mrt.setGlobalFilter}
                    onPaginationChange={mrt.setPagination}
                    onSortingChange={mrt.setSorting}
                    rowCount={mrt.rowCount}
                    initialState={{
                        showColumnFilters: true,
                        density: 'compact',
                        columnPinning: { left: ['mrt-row-select'] },
                    }}
                    state={{
                        columnFilterFns: mrt.columnFilterFns,
                        columnFilters: mrt.columnFilters,
                        globalFilter: mrt.globalFilter,
                        isLoading: mrt.isLoading,
                        pagination: mrt.pagination,
                        showAlertBanner: mrt.isError,
                        showProgressBars: mrt.isRefetching,
                        sorting: mrt.sorting,
                    }}
                    positionToolbarDropZone={'top'}
                    positionExpandColumn={'first'}
                    positionActionsColumn={'first'}
                    positionToolbarAlertBanner="bottom"
                    localization={MRT_Localization_PT_BR}
                />
            </ThemeProvider>
        </div>
    );
};
export default Index;
